'use client';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type {
  CategoriesEntity,
  GeneralLayout,
} from '@eliadmin/core/src/api/general/types/general-layout-types';
import { SocialIcons } from '@eliadmin/core/src/components/header/social-icons';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref } from '@eliadmin/core/src/utils/url-utils';
import { usePathname } from 'next/navigation';
import { Button } from '../ui/button';

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
  generalLayout: GeneralLayout['data'];
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const { t } = useTranslations();
  const pathname = usePathname();
  const generalLayout = props.generalLayout;

  return (
    <>
      <Sheet>
        <SheetTrigger aria-label="Menu" className={cn('ea-mobile-sidebar__trigger', props.className?.trigger)}>
          <Image
            src={'/images/menu.svg'}
            alt="Otvoriť menu"
            height={28}
            width={24}
            className="object-contain"
          />
        </SheetTrigger>
        <SheetContent
          side={props.side}
          className={cn(
            'flex w-[320px] flex-col items-start justify-between px-4 py-8 shadow-none sm:w-[320px]',
            'ea-mobile-sidebar__content',
            props.className?.content,
          )}
          showCloseIcon={false}
        >
          <div className="flex w-full items-center justify-end">
            <SheetClose>
              <Image
                src={'/images/close.svg'}
                alt="Zatvoriť"
                height={28}
                width={28}
                className="object-contain"
              />
            </SheetClose>
          </div>
          <div className="flex w-full flex-col items-center justify-center gap-6">
            {props.menu?.map(item => {
              const isActive = pathname === item?.path;

              return (
                <SheetClose
                  key={item.name}
                  asChild
                  className={cn('text-center text-2xl', isActive ? 'font-bold' : 'font-medium')}
                >
                  <Link prefetch={false} href={getSafeHref(item?.path ?? '#')}>
                    {item?.name}
                  </Link>
                </SheetClose>
              );
            })}
            <SheetClose asChild>
              <Button asChild variant="dark" className="w-full">
                <Link prefetch={false} href="/rezervacia">
                  <Image
                    src="/images/calendar.svg"
                    alt={t('text4069')}
                    width={0}
                    height={0}
                    className="max-h-5 min-h-5 min-w-5 max-w-5 object-contain"
                  />
                  {t('text4069')}
                </Link>
              </Button>
            </SheetClose>
          </div>
          <div className="flex w-full flex-col items-center justify-center gap-6">
            <div className="flex w-full flex-col items-center justify-center gap-2">
              <Link
                prefetch={false}
                target="_blank"
                href={`tel:${generalLayout.contact?.phone}`}
                className="flex items-center justify-start gap-2 font-medium transition-all hover:opacity-80"
              >
                <Image
                  src={'/images/call.svg'}
                  alt="Volať"
                  height={20}
                  width={20}
                  className="object-contain"
                />
                {generalLayout.contact?.phone}
              </Link>
              <Link
                prefetch={false}
                target="_blank"
                href={`mailto:${generalLayout.contact?.email}`}
                className="flex items-center justify-start gap-2 font-medium transition-all hover:opacity-80"
              >
                <Image
                  src={'/images/email.svg'}
                  alt="Napísať"
                  height={20}
                  width={20}
                  className="object-contain"
                />
                {generalLayout.contact?.email}
              </Link>
            </div>
            <SocialIcons
              className={{
                wrapper: 'items-center justify-center gap-4',
                button:
                  'h-14 w-14 border-none bg-primary-foreground text-black shadow-none hover:bg-primary-foreground/75',
                icon: 'stoke-black fill-black text-black',
                iconLink: 'fill-black stroke-black text-black',
              }}
            />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;
